import { Form } from "@unform/web";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { countries_list, getRegistries } from "~/functions";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import { FullGridContext } from "../..";
import { AlertContext } from "~/App";
import {
  Ambulance,
  BadgeDollarSign,
  BookText,
  Contact,
  Download,
  FileInputIcon,
  MessageSquareShare,
  Send,
  User,
} from "lucide-react";
import Input from "~/components/RegisterForm/Input";
import PDFViewer from "~/components/PDFViewer";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import {
  addressOptions,
  dependentRelationshipTypeOptions,
  genderOptions,
  maritalStatusOptions,
  relationshipTypeOptions,
  scheduleOptions,
  sponsorRelationshipTypeOptions,
  yesOrNoOptions,
} from "~/functions/selectPopoverOptions";
import DatePicker from "~/components/RegisterForm/DatePicker";
import { format, isValid, parseISO } from "date-fns";
import PhoneNumberInput from "~/components/RegisterForm/PhoneNumberInput";
import { Scope } from "@unform/core";
import FileInput from "~/components/RegisterForm/FileInput";
import { organizeMultiAndSingleFiles } from "~/functions/uploadFile";
import { pdfjs } from "react-pdf";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import TransferEligibility from "~/pages/Commercial/Enrollments/Preview/TransferEligibility";
import {
  getFilenameFromContentDisposition,
  openBlobInNewTab,
} from "~/utils/blobPreview";
export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  function parseISODateOrNull(value) {
    if (!value) return null;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  }

  const { alertBox } = useContext(AlertContext);
  const [loading, setLoading] = useState(false);
  const { handleOpened, successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    class_number: "",
    status: "Active",
    quantity_of_students: 0,
    students: [],
    levels: [],
    groups: [],
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(true);
  const [activeMenu, setActiveMenu] = useState("student");
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  const auth = useSelector((state) => state.auth);
  const [openedDocumentIndex, setOpenedDocumentIndex] = useState(0);
  const canShowTransferEligibility = [3, 4].includes(
    pageData?.students?.processsubstatuses?.id
  );
  const isTransferLetter = ["transfer", "transfer in"].includes(String(pageData?.students?.processsubstatuses?.name || "").trim().toLowerCase());
  const transferLetterMenu = access?.children?.find(child => child.alias === "send-transfer-acceptance-letter-tab");
  const transferLetterGrant = transferLetterMenu?.MenuHierarchyXGroup || transferLetterMenu || {};
  const canManageAcceptanceLetter = !isTransferLetter || transferLetterGrant.view === true;
  const isRegistrationAuto = pageData.registration_number_mode === "AUTOMATIC";
  const isExStudentCategory =
    String(pageData?.students?.category || "").trim().toLowerCase() ===
    "ex-student";
  const existingRegistrationNumber =
    String(pageData?.students?.registration_number || "").trim() || null;
  const shouldReuseExistingRegistrationNumber =
    isExStudentCategory && Boolean(existingRegistrationNumber);
  const shouldBlockSaveForExistingRegistration =
    Boolean(existingRegistrationNumber) && !isExStudentCategory;
  const [dynamicScheduleOptions, setDynamicScheduleOptions] = useState([]);
  const mergedScheduleOptions = useMemo(() => {
    const baseOptions =
      dynamicScheduleOptions.length > 0 ? dynamicScheduleOptions : scheduleOptions;
    const optionsMap = new Map(
      (baseOptions || [])
        .filter((option) => option && option.value != null && option.value !== "")
        .map((option) => [String(option.value), option])
    );

    const savedPlanSchedule = pageData?.plan_schedule;
    if (
      savedPlanSchedule != null &&
      savedPlanSchedule !== "" &&
      !optionsMap.has(String(savedPlanSchedule))
    ) {
      optionsMap.set(String(savedPlanSchedule), {
        value: savedPlanSchedule,
        label: savedPlanSchedule,
      });
    }

    return Array.from(optionsMap.values());
  }, [dynamicScheduleOptions, pageData?.plan_schedule]);
  const selectedPlanScheduleOption = useMemo(() => {
    const savedPlanSchedule = pageData?.plan_schedule;
    if (savedPlanSchedule != null && savedPlanSchedule !== "") {
      return {
        value: savedPlanSchedule,
        label: savedPlanSchedule,
      };
    }
    return null;
  }, [pageData?.plan_schedule]);
  const transferEnrollment = useMemo(() => {
    if (!pageData?.i20form?.enrollment_id) return pageData;
    return { ...pageData, id: pageData.i20form.enrollment_id };
  }, [pageData]);

  const countriesOptions = countries_list?.map((country) => {
    return { value: country, label: country };
  });

  useEffect(() => {
    setOpenedDocumentIndex(0);
    document.getElementById("scrollPage").scrollTo(0, 0);
  }, [activeMenu]);

  async function handleStartProcess(processType = "") {
    if (!processType) {
      return;
    }
    setLoading(true);
    try {
      await api.post(`/enrollments/start-process/`, {
        student_id: pageData.students.id,
        processType,
      });

      getPageData();
      toast("The process has been started!", {
        autoClose: 1000,
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast("Error!", { autoClose: 1000 });
      console.log(err);
    }
  }

  async function getPageData() {
    try {
      const { data } = await api.get(`/i20pendings/${id}`);
      try {
        // Filter the schedules by the student's enrollment type, otherwise
        // Global schedules leak into the other process substatuses.
        const scheduleSubstatusId = data?.students?.processsubstatuses?.id;
        const { data: scheduleStrings } = await api.get(
          `/filials/${data.filial_id}/plan-schedule-strings`,
          scheduleSubstatusId
            ? { params: { processsubstatus_id: scheduleSubstatusId } }
            : undefined
        );
        const scheduleOptionsFromAPI = (scheduleStrings || []).map((str) => ({
          value: str,
          label: str,
        }));
        setDynamicScheduleOptions(scheduleOptionsFromAPI);
      } catch (err) {
        console.log("Error fetching schedule options:", err);
        setDynamicScheduleOptions([]);
      }
      setPageData({
        ...data,
        loaded: true,
      });
      if (data.i20form.status === "Confirmed") {
        setActiveMenu("i-20");
      }
      const {
        created_by,
        created_at,
        updated_by,
        updated_at,
        canceled_by,
        canceled_at,
      } = data;
      const registries = await getRegistries({
        created_by,
        created_at,
        updated_by,
        updated_at,
        canceled_by,
        canceled_at,
      });
      setRegistry(registries);
    } catch (err) {
      // console.log(err);
      toast(err.response.data.error, { type: "error", autoClose: 3000 });
    }
  }

  const handleOpenPDF = async () => {
    try {
      const endpoint = isNonF1Global
        ? `/pdf/new-enrollment-global/${pageData.i20form.enrollment_id}`
        : `/pdf/new-enrollment/${pageData.i20form.enrollment_id}`;
      const response = await api.get(endpoint, { responseType: "blob" });
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `enrollment_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to generate PDF", {
        type: "error",
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    getPageData();
    setSuccessfullyUpdated(false);
  }, []);

  async function handleGeneralFormSubmit(data) {
    setLoading(true);
    const isAuto =
      (data?.registration_number_mode || pageData.registration_number_mode) ===
      "AUTOMATIC";
    function send(file = null) {
      const sanitizedData = {
        ...data,
        studentData: data.studentData
          ? {
              ...data.studentData,
            }
          : data.studentData,
      };
      if (sanitizedData.studentData) {
        delete sanitizedData.studentData.processtype_name;
        delete sanitizedData.studentData.processsubstatus_name;
      }
      if (sanitizedData.new_program) {
        const normalizeDateField = (value) => {
          if (!value) return null;
          if (value instanceof Date) {
            return isValid(value) ? format(value, "yyyy-MM-dd") : null;
          }
          return value;
        };
        sanitizedData.new_program = {
          ...sanitizedData.new_program,
          start_date: normalizeDateField(sanitizedData.new_program.start_date),
          end_date: normalizeDateField(sanitizedData.new_program.end_date),
        };
      }
      const baseNewProgram = sanitizedData.new_program
        ? { ...sanitizedData.new_program }
        : {};

      if (shouldReuseExistingRegistrationNumber) {
        baseNewProgram.registration_number = existingRegistrationNumber;
      }

      if (isAuto && sanitizedData.new_program) {
        baseNewProgram.registration_number = shouldReuseExistingRegistrationNumber
          ? existingRegistrationNumber
          : null;
      }

      if (
        shouldReuseExistingRegistrationNumber &&
        !baseNewProgram.registration_number
      ) {
        baseNewProgram.registration_number = existingRegistrationNumber;
      }

      const shouldSendNewProgram =
        Boolean(sanitizedData.new_program) ||
        Boolean(file) ||
        shouldReuseExistingRegistrationNumber;

      const endpoint = `/enrollments/${id}`;
      const payload = {
        ...sanitizedData,
        new_program: shouldSendNewProgram
          ? {
              ...baseNewProgram,
              file_id: file || baseNewProgram.file_id || null,
            }
          : null,
      };
      if (shouldReuseExistingRegistrationNumber) {
        if (payload.new_program) {
          payload.new_program.registration_number = existingRegistrationNumber;
        }
        if (payload.studentData) {
          payload.studentData.registration_number = existingRegistrationNumber;
        }
      }
      console.log("[I20 Pending] PUT endpoint:", endpoint);
      console.log(
        "[I20 Pending] registration_number sent:",
        payload?.new_program?.registration_number
      );
      console.log("[I20 Pending] activeMenu:", activeMenu);
      console.log("[I20 Pending] isAuto:", isAuto);
      console.log(
        "[I20 Pending] shouldReuseExistingRegistrationNumber:",
        shouldReuseExistingRegistrationNumber
      );
      console.log("[I20 Pending] PUT payload:", payload);
      api
        .put(endpoint, payload)
        .then((response) => {
          if (activeMenu === "i-20") {
            toast("I-20 Submitted successfully!", { autoClose: 1000 });
            setSuccessfullyUpdated(true);
            handleOpened(null);
            return;
          }
          toast("Saved!", { autoClose: 1000 });
          setSuccessfullyUpdated(true);
          setPageData({
            ...pageData,
            i20form: { ...pageData.i20form, status: "Confirmed" },
          });
          setActiveMenu("i-20");
        })
        .catch((err) => {
          const message =
            err?.response?.data?.error ||
            "Failed to save enrollment. Please try again.";
          toast(message, { type: "error", autoClose: 3000 });
        })
        .finally(() => {
          setLoading(false);
        });
    }
    if (activeMenu === "i-20") {
      if (data.new_program.file_id) {
        const allPromises = organizeMultiAndSingleFiles(
          [data.new_program],
          "StudentPrograms"
        );

        Promise.all(allPromises).then(async (files) => {
          files?.map(async (file) => {
            if (!file) {
              return;
            }
            send(file);
          });
        });
      } else {
        send();
      }
    } else {
      send();
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  const studentDocuments = useMemo(() => {
    if (!pageData.enrollmentdocuments) return [];
    return pageData.enrollmentdocuments.filter(
      (doc) => doc.documents?.subtype === "Student"
    );
  }, [pageData.enrollmentdocuments]);

  const sponsorDocuments = useMemo(() => {
    if (!pageData.enrollmentdocuments) return [];
    return pageData.enrollmentdocuments.filter(
      (doc) => doc.documents?.subtype === "Sponsor"
    );
  }, [pageData.enrollmentdocuments]);

  async function handleSendAcceptanceLetter() {
    setLoading(true);
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to send the acceptance letter? This action cannot be undone.",
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.post(
                `/send-acceptance-letter/${pageData.i20form.enrollment_id}`,
                {
                  origin: "I-20",
                }
              );
              toast("Acceptance letter sent successfully!", {
                autoClose: 1000,
              });
              setLoading(false);
            } catch (err) {
              setLoading(false);
              if (err?.response?.data?.error) {
                toast(err.response.data.error, {
                  type: "error",
                  autoClose: 3000,
                });
              }
            }
          },
        },
      ],
    });
  }

  const handlePrintAcceptanceLetter = async () => {
    try {
      const response = await api.get(
        `/acceptance-letter/pdf/${pageData.i20form.enrollment_id}`,
        { responseType: "blob" }
      );
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `acceptance_letter_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (err) {
      let errorMessage = err?.response?.data?.error;
      if (err?.response?.data instanceof Blob) {
        try { errorMessage = JSON.parse(await err.response.data.text()).error; } catch {}
      }
      toast(errorMessage || "Failed to generate PDF", {
        type: "error",
        autoClose: 3000,
      });
    }
  };

  const isNonF1Global =
    pageData?.students?.processtypes?.name === "Non-F1" &&
    pageData?.students?.processsubstatuses?.name === "Global";

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="student"
            >
              <User size={16} /> Student Information
            </RegisterFormMenu>
            {!isNonF1Global && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="emergency-contact"
              >
                <Ambulance size={16} /> Emergency Contact
              </RegisterFormMenu>
            )}
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="enrollment"
            >
              <BookText size={16} /> Enrollment
            </RegisterFormMenu>
            {!isNonF1Global && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="dependents"
              >
                <Contact size={16} /> Dependents
              </RegisterFormMenu>
            )}
            {!isNonF1Global && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="affidavit-of-support"
              >
                <BadgeDollarSign size={16} /> Affidavit of Support
              </RegisterFormMenu>
            )}
            <RegisterFormMenu
              setActiveMenu={() => handleOpenPDF()}
              activeMenu={activeMenu}
              name="pdf"
            >
              <FileInputIcon size={16} />{" "}
              {isNonF1Global ? "New PDF" : "Enrollment PDF"}
            </RegisterFormMenu>

            {["Confirmed", "Concluded"].includes(pageData?.i20form?.status) && (
              <>
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="i-20"
                >
                  <FileInputIcon size={16} />{" "}
                  {isNonF1Global ? "Student Registration" : "I-20"}
                </RegisterFormMenu>
                {pageData?.students?.programs[0]?.i20 && canManageAcceptanceLetter && (
                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="acceptance-letter"
                  >
                    <Send size={16} /> Acceptance Letter
                  </RegisterFormMenu>
                )}
                {canShowTransferEligibility && (
                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="transfer-eligibility"
                  >
                    <Send size={16} /> Transfer Eligibility
                  </RegisterFormMenu>
                )}
              </>
            )}
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <InputContext.Provider
              value={{
                id,
                generalForm,
                setSuccessfullyUpdated,
                fullscreen,
                setFullscreen,
                successfullyUpdated,
                handleCloseForm,
                handleInactivate: () => null,
                canceled: pageData.canceled_at,
              }}
            >
              <div
                id="scrollPage"
                className="flex flex-col items-start justify-start text-sm overflow-y-scroll"
              >
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  {pageData.loaded ? (
                    <>
                      <FormHeader
                        loading={loading}
                        access={
                          shouldBlockSaveForExistingRegistration
                            ? {
                                MenuHierarchyXGroup: {
                                  create: false,
                                  edit: false,
                                },
                              }
                            : { ...access, edit: false }
                        }
                        title={`${
                          pageData.subject
                            ? pageData.subject
                            : "Enrollment Process"
                        }`}
                        registry={registry}
                        InputContext={InputContext}
                        saveText={`${
                          activeMenu !== "i-20"
                            ? "Save & Confirm"
                            : "Submit I-20"
                        }`}
                        saveIcon="Send"
                        enableFullScreen={false}
                      />
                      <InputLineGroup
                        title="student"
                        activeMenu={activeMenu === "student"}
                      >
                        <div className="w-full flex flex-row justify-between items-start gap-4">
                          <div className="flex-1 flex flex-col justify-start items-start overflow-y-scroll h-[1040px]">
                            <Scope path={`studentData`}>
                              <InputLine title={`General Data`}>
                                <Input
                                  type="hidden"
                                  name="id"
                                  defaultValue={pageData.students.id}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="processtype_name"
                                  readOnly
                                  disabled
                                  grow
                                  title="Process Type"
                                  defaultValue={
                                    pageData.students.processtypes?.name || ""
                                  }
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="processsubstatus_name"
                                  readOnly
                                  disabled
                                  grow
                                  title="Process Substatus"
                                  defaultValue={
                                    pageData.students.processsubstatuses?.name ||
                                    ""
                                  }
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              {isNonF1Global && (
                                <InputLine>
                                  <DatePicker
                                    name="session_start_date"
                                    required
                                    grow
                                    title="Session Start Date"
                                    defaultValue={
                                      pageData.session_start_date
                                        ? parseISO(pageData.session_start_date)
                                        : null
                                    }
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                  <DatePicker
                                    name="session_end_date"
                                    required
                                    grow
                                    title="Session End Date"
                                    defaultValue={
                                      pageData.session_end_date
                                        ? parseISO(pageData.session_end_date)
                                        : null
                                    }
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                              )}
                              <InputLine>
                                <Input
                                  type="text"
                                  name="name"
                                  required
                                  grow
                                  title="Name"
                                  defaultValue={pageData.students.name}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <Input
                                  type="text"
                                  name="email"
                                  readOnly
                                  disabled
                                  grow
                                  title="Email"
                                  defaultValue={pageData.students.email}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <Input
                                  type="text"
                                  name="last_name"
                                  required
                                  grow
                                  title="Last Name"
                                  defaultValue={pageData.students.last_name}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <DatePicker
                                  name="date_of_birth"
                                  required
                                  grow
                                  title="Birth Date"
                                  defaultValue={
                                    pageData.students.date_of_birth
                                      ? parseISO(
                                          pageData.students.date_of_birth
                                        )
                                      : null
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <SelectPopover
                                  name="marital_status"
                                  required
                                  grow
                                  title="Marital Status"
                                  isSearchable
                                  defaultValue={maritalStatusOptions.find(
                                    (maritalStatus) =>
                                      maritalStatus.value ===
                                      pageData.students.marital_status
                                  )}
                                  options={maritalStatusOptions}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine title="Birth Location">
                                <Input
                                  type="text"
                                  name="birth_city"
                                  required
                                  grow
                                  title="Birth City"
                                  defaultValue={pageData.students.birth_city}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <Input
                                  type="text"
                                  name="birth_state"
                                  required
                                  grow
                                  title="Birth State"
                                  defaultValue={pageData.students.birth_state}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <SelectPopover
                                  name="birth_country"
                                  required
                                  grow
                                  title="Birth Country"
                                  isSearchable
                                  defaultValue={countriesOptions.find(
                                    (country) =>
                                      country.value ===
                                      pageData.students.birth_country
                                  )}
                                  options={countriesOptions}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <Input
                                  type="text"
                                  name="native_language"
                                  required
                                  grow
                                  title="Native Language"
                                  defaultValue={
                                    pageData.students.native_language
                                  }
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <SelectPopover
                                  name="citizen_country"
                                  required
                                  grow
                                  title="Citizen Country"
                                  isSearchable
                                  defaultValue={countriesOptions.find(
                                    (country) =>
                                      country.value ===
                                      pageData.students.citizen_country
                                  )}
                                  options={countriesOptions}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine title="Documentation">
                                <Input
                                  type="text"
                                  name="passport_number"
                                  required
                                  grow
                                  title="Passport Number"
                                  placeholder="-----"
                                  defaultValue={
                                    pageData.students.passport_number
                                  }
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <DatePicker
                                  name="passport_expiration_date"
                                  required
                                  grow
                                  title="Passport Expiration Date"
                                  defaultValue={
                                    pageData.students.passport_expiration_date
                                      ? parseISO(
                                          pageData.students
                                            .passport_expiration_date
                                        )
                                      : null
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                {pageData.students.sub_status ===
                                  "Change of Visa Status" && (
                                  <DatePicker
                                    name="i94_expiration_date"
                                    required
                                    grow
                                    title="I94 Expiration Date"
                                    defaultValue={
                                      pageData.students.i94_expiration_date
                                        ? parseISO(
                                            pageData.students
                                              .i94_expiration_date
                                          )
                                        : null
                                    }
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                )}
                              </InputLine>
                              {pageData.students.sub_status !== "Initial" && (
                                <InputLine title="Admission Correspondence">
                                  <SelectPopover
                                    name="admission_correspondence_address"
                                    required
                                    grow
                                    title="Please check the box where you wish your admission correspondence to be mailed"
                                    options={addressOptions}
                                    defaultValue={addressOptions.find(
                                      (address) =>
                                        address.value ===
                                        pageData.admission_correspondence_address
                                    )}
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                              )}
                              {pageData.students.sub_status !==
                                "Transfer In" && (
                                <>
                                  <InputLine title="Address in Home Country">
                                    <PhoneNumberInput
                                      type="text"
                                      name="home_country_phone"
                                      grow
                                      title="Phone Number"
                                      value={pageData.students?.home_country_phone?.replaceAll(
                                        " ",
                                        ""
                                      )}
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                  <InputLine>
                                    <Input
                                      type="text"
                                      name="home_country_address"
                                      grow
                                      title="Address"
                                      defaultValue={
                                        pageData.students.home_country_address
                                      }
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                  <InputLine>
                                    <Input
                                      type="text"
                                      name="home_country_zip"
                                      grow
                                      title="Zip Code"
                                      defaultValue={
                                        pageData.students.home_country_zip
                                      }
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                  <InputLine>
                                    <Input
                                      type="text"
                                      name="home_country_city"
                                      grow
                                      title="City"
                                      defaultValue={
                                        pageData.students.home_country_city
                                      }
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                  <InputLine>
                                    <Input
                                      type="text"
                                      name="home_country_state"
                                      grow
                                      title="State"
                                      defaultValue={
                                        pageData.students.home_country_state
                                      }
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                  <InputLine>
                                    <SelectPopover
                                      name="home_country_country"
                                      grow
                                      title="Country"
                                      isSearchable
                                      defaultValue={countriesOptions?.find(
                                        (country) =>
                                          country.value ===
                                          pageData.students.home_country_country
                                      )}
                                      options={countriesOptions}
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                </>
                              )}
                              {pageData.students.sub_status !== "Initial" && (
                                <>
                                  <InputLine title="Address in United States">
                                    <PhoneNumberInput
                                      type="text"
                                      name="phone"
                                      grow
                                      title="USA Phone Number"
                                      value={pageData.students.phone?.replaceAll(
                                        " ",
                                        ""
                                      )}
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                  <InputLine>
                                    <Input
                                      type="text"
                                      name="address"
                                      grow
                                      title="USA Address"
                                      defaultValue={pageData.students.address}
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                  <InputLine>
                                    <Input
                                      type="text"
                                      name="zip"
                                      grow
                                      title="USA Zip Code"
                                      isZipCode
                                      defaultValue={pageData.students.zip}
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                </>
                              )}
                              {pageData.students.sub_status !== "Initial" && (
                                <>
                                  <InputLine>
                                    <Input
                                      type="text"
                                      name="city"
                                      grow
                                      title="USA City"
                                      defaultValue={pageData.students.city}
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                  <InputLine>
                                    <Input
                                      type="text"
                                      name="state"
                                      grow
                                      title="USA State"
                                      defaultValue={pageData.students.state}
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                </>
                              )}
                            </Scope>
                          </div>

                          <div className="flex-1 max-w-4xl h-[1040px]">
                            <div className="flex flex-row items-center justify-center gap-2">
                              <InputLine title={`Documents`}>
                                {pageData.enrollmentdocuments &&
                                  pageData.enrollmentdocuments.length > 0 &&
                                  pageData.enrollmentdocuments
                                    .filter(
                                      (doc) =>
                                        doc.documents?.subtype === "Student"
                                    )
                                    ?.map((enrollmentdocument, index) => (
                                      <button
                                        key={index}
                                        type="button"
                                        onClick={() =>
                                          setOpenedDocumentIndex(index)
                                        }
                                        className={`${
                                          openedDocumentIndex === index
                                            ? "bg-primary text-white"
                                            : "bg-secondary text-primary"
                                        } rounded-md py-4 px-8 my-2 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1`}
                                      >
                                        {
                                          enrollmentdocument?.documents
                                            ?.short_name
                                        }
                                      </button>
                                    ))}
                              </InputLine>
                            </div>

                            {studentDocuments.map(
                              (enrollmentdocument, index) => {
                                if (openedDocumentIndex === index) {
                                  return (
                                    <div
                                      key={`pdf-${enrollmentdocument.id}`}
                                      className="overflow-y-scroll h-[940px]"
                                    >
                                      <PDFViewer
                                        key={`pdf-${enrollmentdocument.id}`}
                                        download={true}
                                        file={{
                                          url: enrollmentdocument.file?.url,
                                        }}
                                        height={900}
                                      />
                                    </div>
                                  );
                                }
                                return null;
                              }
                            )}
                          </div>
                        </div>
                      </InputLineGroup>
                      <InputLineGroup
                        title="Emergency Contact"
                        activeMenu={activeMenu === "emergency-contact"}
                      >
                        <Scope path={`emergencyData`}>
                          <InputLine title="Emergency Contact">
                            <Input
                              type="hidden"
                              name="id"
                              defaultValue={
                                pageData.enrollmentemergencies.length > 0
                                  ? pageData.enrollmentemergencies[0].id
                                  : ""
                              }
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="name"
                              required={!isNonF1Global}
                              grow
                              title="Full Name"
                              defaultValue={
                                pageData.enrollmentemergencies.length > 0
                                  ? pageData.enrollmentemergencies[0].name
                                  : ""
                              }
                              InputContext={InputContext}
                            />
                            {pageData.enrollmentemergencies.length > 0 ? (
                              <SelectPopover
                                name="relationship_type"
                                required={!isNonF1Global}
                                grow
                                title="Relationship Type"
                                options={relationshipTypeOptions}
                                isSearchable
                                defaultValue={relationshipTypeOptions.find(
                                  (relationshipType) =>
                                    relationshipType.value ===
                                    pageData.enrollmentemergencies[0]
                                      .relationship_type
                                )}
                                InputContext={InputContext}
                              />
                            ) : (
                              <SelectPopover
                                name="relationship_type"
                                required={!isNonF1Global}
                                grow
                                title="Relationship Type"
                                options={relationshipTypeOptions}
                                isSearchable
                                InputContext={InputContext}
                              />
                            )}
                          </InputLine>
                          <InputLine>
                            <Input
                              type="text"
                              name="email"
                              required={!isNonF1Global}
                              grow
                              title="E-mail"
                              defaultValue={
                                pageData.enrollmentemergencies.length > 0
                                  ? pageData.enrollmentemergencies[0].email
                                  : ""
                              }
                              InputContext={InputContext}
                            />
                            <PhoneNumberInput
                              type="text"
                              name="phone"
                              required={!isNonF1Global}
                              grow
                              title="Phone Number"
                              value={
                                pageData.enrollmentemergencies.length > 0
                                  ? pageData.enrollmentemergencies[0]?.phone?.replaceAll(
                                      " ",
                                      ""
                                    )
                                  : ""
                              }
                              InputContext={InputContext}
                            />
                          </InputLine>
                        </Scope>
                      </InputLineGroup>
                      <InputLineGroup
                        title="enrollment"
                        activeMenu={activeMenu === "enrollment"}
                      >
                        <Scope path={`enrollmentData`}>
                          <InputLine title="Enrollment Information">
                            {!isNonF1Global && (
                              <Input
                                type="text"
                                name="plan_months"
                                required
                                title="How many months do you plan to study?"
                                defaultValue={pageData.plan_months}
                                InputContext={InputContext}
                              />
                            )}
                            {isNonF1Global ? (
                              <Input
                                type="text"
                                name="plan_schedule"
                                readOnly
                                grow
                                title="What is your plan schedule?"
                                defaultValue={pageData.plan_schedule}
                                InputContext={InputContext}
                              />
                            ) : (
                              <SelectPopover
                                name="plan_schedule"
                                required
                                grow
                                title="What is your plan schedule?"
                                options={mergedScheduleOptions}
                                isSearchable
                                defaultValue={selectedPlanScheduleOption}
                                InputContext={InputContext}
                              />
                            )}
                            <DatePicker
                              name="plan_date"
                              required
                              grow
                              title={
                                isNonF1Global
                                  ? "Expected Start Date"
                                  : "What date do you wish to begin classes (M)?"
                              }
                              defaultValue={
                                pageData.plan_date
                                  ? parseISO(pageData.plan_date)
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                            {isNonF1Global && (
                              <>
                                <DatePicker
                                  name="session_start_date"
                                  required
                                  grow
                                  title="Session Start Date"
                                  defaultValue={
                                    pageData.session_start_date
                                      ? parseISO(pageData.session_start_date)
                                      : null
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  name="session_end_date"
                                  required
                                  grow
                                  title="Session End Date"
                                  defaultValue={
                                    pageData.session_end_date
                                      ? parseISO(pageData.session_end_date)
                                      : null
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                              </>
                            )}
                          </InputLine>
                        </Scope>
                      </InputLineGroup>
                      <InputLineGroup
                        title="Dependent Information"
                        activeMenu={activeMenu === "dependents"}
                      >
                        {pageData.enrollmentdependents.length === 0 && (
                          <InputLine title="Dependent Information">
                            <p>No dependents.</p>
                          </InputLine>
                        )}
                        {pageData.enrollmentdependents?.map(
                          (dependent, index) => {
                            return (
                              <div
                                key={index}
                                className="w-full flex flex-row justify-between items-start gap-4 pb-8"
                              >
                                <Scope path={`dependentsData[${index}]`}>
                                  <div className="flex-1 flex flex-col justify-start items-start">
                                    <InputLine title={`Dependent ${index + 1}`}>
                                      <Input
                                        type="hidden"
                                        name="id"
                                        defaultValue={dependent.id}
                                        InputContext={InputContext}
                                      />
                                    </InputLine>
                                    <InputLine>
                                      <Input
                                        type="text"
                                        name="name"
                                        required
                                        grow
                                        title="Full Name"
                                        defaultValue={dependent.name}
                                        InputContext={InputContext}
                                      />
                                    </InputLine>
                                    <InputLine>
                                      <SelectPopover
                                        name="gender"
                                        required
                                        grow
                                        title="Gender"
                                        options={genderOptions}
                                        isSearchable
                                        defaultValue={genderOptions.find(
                                          (gender) =>
                                            gender.value === dependent.gender
                                        )}
                                        InputContext={InputContext}
                                      />
                                    </InputLine>
                                    <InputLine>
                                      <SelectPopover
                                        name="relationship_type"
                                        required
                                        grow
                                        title="Relationship Type"
                                        options={
                                          dependentRelationshipTypeOptions
                                        }
                                        isSearchable
                                        defaultValue={dependentRelationshipTypeOptions.find(
                                          (relationshipType) =>
                                            relationshipType.value ===
                                            dependent.relationship_type
                                        )}
                                        InputContext={InputContext}
                                      />
                                    </InputLine>
                                    <InputLine>
                                      <Input
                                        type="text"
                                        name="email"
                                        required
                                        grow
                                        title="E-mail"
                                        defaultValue={dependent.email}
                                        InputContext={InputContext}
                                      />
                                    </InputLine>
                                    <InputLine>
                                      <PhoneNumberInput
                                        type="text"
                                        name="phone"
                                        required
                                        grow
                                        title="Phone Number"
                                        value={dependent?.phone?.replaceAll(
                                          " ",
                                          ""
                                        )}
                                        InputContext={InputContext}
                                      />
                                    </InputLine>
                                  </div>
                                </Scope>
                                <div className="flex-1 max-w-4xl h-[1040px]">
                                  <div className="flex flex-row items-center justify-center gap-2">
                                    <InputLine title={`Documents`}>
                                      {dependent.documents &&
                                        dependent.documents.length > 0 &&
                                        dependent.documents?.map(
                                          (depDoc, index) => (
                                            <button
                                              key={index}
                                              type="button"
                                              onClick={() =>
                                                setOpenedDocumentIndex(index)
                                              }
                                              className={`bg-primary text-white
                                               rounded-md py-4 px-8 my-2 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1`}
                                            >
                                              {depDoc.documents?.short_name}
                                            </button>
                                          )
                                        )}
                                    </InputLine>
                                  </div>

                                  {dependent.documents?.map(
                                    (enrollmentdocument, index) => {
                                      if (openedDocumentIndex === index) {
                                        return (
                                          <div
                                            key={`container-${enrollmentdocument.id}`}
                                            className="overflow-y-scroll h-[940px]"
                                          >
                                            <PDFViewer
                                              // A KEY deve estar no componente que carrega o PDF
                                              key={`pdf-viewer-${
                                                enrollmentdocument.file?.id ||
                                                enrollmentdocument.id
                                              }`}
                                              download={true}
                                              file={{
                                                url: enrollmentdocument.file
                                                  ?.url,
                                              }}
                                              height={900}
                                            />
                                          </div>
                                        );
                                      }
                                      return null;
                                    }
                                  )}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </InputLineGroup>
                      <InputLineGroup
                        title="Affidavit of Support"
                        activeMenu={activeMenu === "affidavit-of-support"}
                      >
                        {pageData.enrollmentsponsors &&
                          pageData.enrollmentsponsors
                            ?.filter((sponsor) => sponsor.name)
                            ?.map((sponsor, index) => {
                              if (sponsor) {
                                return (
                                  <div
                                    key={index}
                                    className="w-full flex flex-row justify-between items-start gap-4"
                                  >
                                    <div className="flex-1 flex flex-col justify-start items-start overflow-y-scroll h-[1040px]">
                                      <Scope
                                        key={index}
                                        path={`sponsorsData[${index}]`}
                                      >
                                        <InputLine
                                          title={`Sponsor ${index + 1}`}
                                        >
                                          <Input
                                            type="hidden"
                                            name="id"
                                            defaultValue={sponsor.id}
                                            InputContext={InputContext}
                                          />
                                          <Input
                                            type="text"
                                            name="name"
                                            required
                                            grow
                                            title="Full Name"
                                            defaultValue={sponsor.name}
                                            InputContext={InputContext}
                                          />
                                          <SelectPopover
                                            name="relationship_type"
                                            required
                                            grow
                                            title="Relationship Type"
                                            options={
                                              sponsorRelationshipTypeOptions
                                            }
                                            isSearchable
                                            defaultValue={
                                              sponsorRelationshipTypeOptions.find(
                                                (relationshipType) =>
                                                  relationshipType.value ===
                                                  sponsor.relationship_type
                                              ) ||
                                              sponsorRelationshipTypeOptions[
                                                sponsorRelationshipTypeOptions.length -
                                                  1
                                              ]
                                            }
                                            InputContext={InputContext}
                                          />
                                        </InputLine>
                                        <InputLine>
                                          <Input
                                            type="text"
                                            name="email"
                                            required
                                            grow
                                            title="E-mail"
                                            defaultValue={sponsor.email}
                                            InputContext={InputContext}
                                          />
                                          <PhoneNumberInput
                                            type="text"
                                            name="phone"
                                            required
                                            grow
                                            title="Phone Number"
                                            value={sponsor?.phone?.replaceAll(
                                              " ",
                                              ""
                                            )}
                                            InputContext={InputContext}
                                          />
                                        </InputLine>
                                      </Scope>
                                    </div>

                                    <div className="flex-1 max-w-4xl h-[1040px]">
                                      <div className="flex flex-row items-center justify-center gap-2">
                                        <InputLine title={`Documents`}>
                                          {pageData.enrollmentdocuments &&
                                            pageData.enrollmentdocuments
                                              .length > 0 &&
                                            pageData.enrollmentdocuments
                                              .filter(
                                                (doc) =>
                                                  doc.documents?.subtype ===
                                                  "Sponsor"
                                              )
                                              ?.map(
                                                (enrollmentdocument, index) => (
                                                  <button
                                                    key={index}
                                                    type="button"
                                                    onClick={() =>
                                                      setOpenedDocumentIndex(
                                                        index
                                                      )
                                                    }
                                                    className={`${
                                                      openedDocumentIndex ===
                                                      index
                                                        ? "bg-primary text-white"
                                                        : "bg-secondary text-primary"
                                                    } rounded-md py-4 px-8 my-2 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1`}
                                                  >
                                                    {
                                                      enrollmentdocument
                                                        ?.documents?.short_name
                                                    }
                                                  </button>
                                                )
                                              )}
                                        </InputLine>
                                      </div>

                                      {sponsorDocuments.map(
                                        (sponsorDocument, index) => {
                                          if (openedDocumentIndex === index) {
                                            return (
                                              <div
                                                key={`pdf-${sponsorDocument.id}`}
                                                className="overflow-y-scroll h-[940px]"
                                              >
                                                <PDFViewer
                                                  key={`pdf-${sponsorDocument.id}`}
                                                  download={true}
                                                  file={{
                                                    url: sponsorDocument.file
                                                      ?.url,
                                                  }}
                                                  height={900}
                                                />
                                              </div>
                                            );
                                          }
                                          return null;
                                        }
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                            })}
                      </InputLineGroup>
                      {["Confirmed", "Concluded"].includes(
                        pageData?.i20form?.status
                      ) && (
                        <InputLineGroup
                          title="I-20"
                          activeMenu={activeMenu === "i-20"}
                        >
                          <Scope path="new_program">
                            <InputLine
                              title={
                                isNonF1Global
                                  ? "Submit Student Registration"
                                  : "Submit I-20"
                              }
                            >
                              <Input
                                type="text"
                                name="registration_number"
                                required={!isRegistrationAuto}
                                grow
                                title="Registration Number"
                                defaultValue={
                                  isRegistrationAuto
                                    ? shouldReuseExistingRegistrationNumber
                                      ? existingRegistrationNumber
                                      : "Automatic"
                                    : pageData.students.registration_number
                                }
                                readOnly={isRegistrationAuto}
                                InputContext={InputContext}
                              />
                              {pageData.students.processtypes.name === "F1" && (
                                <>
                                  <Input
                                    type="text"
                                    name="nsevis"
                                    required
                                    grow
                                    title="SEVIS Number"
                                    defaultValue={pageData.students.nsevis}
                                    InputContext={InputContext}
                                  />
                                  {pageData?.students?.programs[0]?.i20 ? (
                                    <button
                                      type="button"
                                      disabled={loading}
                                      onClick={() =>
                                        window.open(
                                          pageData?.students?.programs[0]?.i20
                                            ?.url
                                        )
                                      }
                                      className={`bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 mt-4 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2 cursor-pointer`}
                                    >
                                      <Download size={14} />
                                      <strong>Open I-20</strong>
                                    </button>
                                  ) : (
                                    <FileInput
                                      name="file_id"
                                      title="I-20"
                                      required
                                      grow
                                      InputContext={InputContext}
                                    />
                                  )}
                                  <DatePicker
                                    name="start_date"
                                    required
                                    grow
                                    title="Program Start Date"
                                    defaultValue={parseISODateOrNull(
                                      pageData?.students?.programs[0]
                                        ?.start_date
                                    )}
                                    placeholderText="MM/DD/YYYY"
                                    onChangeRaw={(e) => e.preventDefault()}
                                    onKeyDown={(e) => e.preventDefault()}
                                    InputContext={InputContext}
                                  />
                                  <DatePicker
                                    name="end_date"
                                    required
                                    grow
                                    title="Program End Date"
                                    defaultValue={parseISODateOrNull(
                                      pageData?.students?.programs[0]?.end_date
                                    )}
                                    placeholderText="MM/DD/YYYY"
                                    onChangeRaw={(e) => e.preventDefault()}
                                    onKeyDown={(e) => e.preventDefault()}
                                    InputContext={InputContext}
                                  />
                                </>
                              )}
                              {isNonF1Global && (
                                <>
                                  <DatePicker
                                    name="session_start_date"
                                    required
                                    grow
                                    title="Session Start Date"
                                    defaultValue={
                                      pageData.session_start_date
                                        ? parseISO(pageData.session_start_date)
                                        : null
                                    }
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                  <DatePicker
                                    name="session_end_date"
                                    required
                                    grow
                                    title="Session End Date"
                                    defaultValue={
                                      pageData.session_end_date
                                        ? parseISO(pageData.session_end_date)
                                        : null
                                    }
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                </>
                              )}
                            </InputLine>
                          </Scope>
                        </InputLineGroup>
                      )}
                      {activeMenu === "transfer-eligibility" && (
                        <InputLineGroup
                          title="Transfer Eligibility"
                          activeMenu={activeMenu === "transfer-eligibility"}
                        >
                          <TransferEligibility
                              access={access}
                            enrollment={transferEnrollment}
                            student_id={pageData?.students?.id}
                            loading={loading}
                            setLoading={setLoading}
                            handleStartProcess={handleStartProcess}
                            hideStatusHeader={true}
                            hideStudentActions={true}
                          />
                        </InputLineGroup>
                      )}
                      {activeMenu === "acceptance-letter" && canManageAcceptanceLetter && (
                        <InputLineGroup
                          title="Acceptance Letter"
                          activeMenu={activeMenu === "acceptance-letter"}
                        >
                          <div className="flex flex-1 w-full flex-col items-start justify-start text-center gap-4 px-4">
                            <div className="flex w-full flex-row items-center justify-start text-center gap-4 border border-gray-200 bg-slate-50 rounded-md p-4">
                              <button
                                type="button"
                                disabled={loading || !canManageAcceptanceLetter}
                                onClick={handleSendAcceptanceLetter}
                                className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
                              >
                                <Send size={14} />
                                <strong>Send Acceptance Letter</strong>
                              </button>
                              <button
                                type="button"
                                disabled={loading || !canManageAcceptanceLetter}
                                onClick={handlePrintAcceptanceLetter}
                                className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
                              >
                                <Download size={14} />
                                <strong>Print</strong>
                              </button>
                            </div>
                          </div>
                        </InputLineGroup>
                      )}
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </Form>
              </div>
            </InputContext.Provider>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
